import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useNotification } from './useNotification';
import { INSIGHTS_QUERY_KEY } from '@insights_module/constants/queryKeys';
import { ExportTranscriptFormat } from '@insights_module/types/Episode';
import {
  fetchDiarizedTranscriptForEpisode,
  fetchTranscriptForEpisode,
  fetchVTTTranscriptForEpisode,
} from '@services/index';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { download } from '@utils/download';
import { formatDiarizedTranscriptTXTFile } from '@insights_module/utils/episodesUtils';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';

export const EXPORT_TRANSCRIPT_FORMAT = {
  TEXT: 'plain_text' as ExportTranscriptFormat,
  PARAGRAPHS: 'paragraphs' as ExportTranscriptFormat,
  RAW: 'raw' as ExportTranscriptFormat,
  VTT: 'vtt' as ExportTranscriptFormat,
};

const FILE_EXTENSION = {
  [EXPORT_TRANSCRIPT_FORMAT.TEXT]: 'txt',
  [EXPORT_TRANSCRIPT_FORMAT.PARAGRAPHS]: 'txt',
  [EXPORT_TRANSCRIPT_FORMAT.RAW]: 'json',
  [EXPORT_TRANSCRIPT_FORMAT.VTT]: 'vtt',
};

export const useTranscriptDownload = (id?: string) => {
  const { t } = useTranslation();
  const { selectedEpisode, setIsTranscriptDownloadInProgress } = useInsightsContext();
  const episodeId = id || selectedEpisode;

  const { notify, NTFC_TYPE } = useNotification();
  const [format, setFormat] = useState<ExportTranscriptFormat | null>(null);
  const { hasDiarizedTranscript } = useEpisodeDetailsContext();

  useQuery({
    queryKey: [INSIGHTS_QUERY_KEY.export_diarized_transcript, episodeId, format],
    queryFn: () => fetchDiarizedTranscriptForEpisode(episodeId),
    enabled: !!episodeId && hasDiarizedTranscript && format !== EXPORT_TRANSCRIPT_FORMAT.VTT,
    staleTime: 0,
    onSuccess: async (response) => {
      if (!format) return;

      const result = await response?.data;
      const diarizedTXT = formatDiarizedTranscriptTXTFile(result.transcript.results.items);
      const url = window.URL.createObjectURL(new Blob([diarizedTXT]));

      download(url, `diarized_transcript_${episodeId}.${FILE_EXTENSION[format]}`);
    },
    onError: (error: { detail: { message: string } }) => {
      notify({
        type: NTFC_TYPE.WARNING,
        message: error.detail.message,
      });
    },
    onSettled: () => {
      setFormat(null);
      setIsTranscriptDownloadInProgress(false);
    },
  });

  useQuery({
    queryKey: [INSIGHTS_QUERY_KEY.export_transcript, episodeId, format],
    queryFn: () => fetchTranscriptForEpisode(episodeId, { format }, 'blob'),
    enabled:
      !!episodeId && !!format && format !== EXPORT_TRANSCRIPT_FORMAT.VTT && !hasDiarizedTranscript,
    staleTime: 0,
    onSuccess: async (response) => {
      if (!format) return;

      const result = await response?.data?.text();
      const transcript = JSON.parse(result)?.transcript;
      const url = window.URL.createObjectURL(new Blob([JSON.stringify(transcript)]));

      download(url, `${episodeId}.${FILE_EXTENSION[format]}`);
    },
    onError: () => {
      notify({
        type: NTFC_TYPE.DANGER,
        message: t('insights.episode_details_drawer.export_transcript_modal.error_message'),
      });
    },
    onSettled: () => {
      setFormat(null);
      setIsTranscriptDownloadInProgress(false);
    },
  });

  useQuery({
    queryKey: [INSIGHTS_QUERY_KEY.export_transcript, episodeId, format],
    queryFn: () => fetchVTTTranscriptForEpisode(episodeId, 'blob'),
    enabled: !!episodeId && format === EXPORT_TRANSCRIPT_FORMAT.VTT,
    staleTime: 0,
    onSuccess: async (response) => {
      if (!format || format !== EXPORT_TRANSCRIPT_FORMAT.VTT) return;
      const url = window.URL.createObjectURL(new Blob([response?.data]));

      download(url, `${episodeId}.${FILE_EXTENSION[format]}`);
    },
    onError: () => {
      notify({
        type: NTFC_TYPE.DANGER,
        message: t('insights.episode_details_drawer.export_transcript_modal.error_message'),
      });
    },
    onSettled: () => {
      setFormat(null);
      setIsTranscriptDownloadInProgress(false);
    },
  });

  const downloadTranscript = (format: ExportTranscriptFormat) => {
    setFormat(format);
    setIsTranscriptDownloadInProgress(true);
  };

  return { downloadTranscript, isLoading: !!format };
};
